import { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { isExpiredSessionErrorResponse, isSessionTokenError } from '../services/responseUtils';
import AuthenticatedRouteLanguageContext from '../services/AuthenticatedRouteLanguageContext';
import { AuthenticatedPaths } from '../constants/Paths';
import FeatureNames from '../constants/FeatureNames';
import { getUserSessionContextGracefully } from '../veevaIdApi';
import getHomeData from '../services/getHomeData';
import InvalidSessionDialog from './InvalidSessionDialog';
import SessionExpiredDialog from './SessionExpiredDialog';
import { isMobile } from '../services/DeviceInformation';
import Header from './Header/Header';
import Loading from './Loading';

const UserSessionContext = createContext({
    handleResponseSessionErrors: (response) => {},
    enableVeevaIdUsernameUpdate: false,
    systemsData: undefined,
    showStudiesTab: undefined,
    loading: true,
});

export const useUserSessionContext = () =>
    useContext(UserSessionContext) ?? {
        handleResponseSessionErrors: () => {},
        enableVeevaIdUsernameUpdate: false,
        systemsData: undefined,
        showStudiesTab: undefined,
        loading: true,
    };

const handleDynamicRouting = (history, hasStudiesTab) => {
    const currentPath = history?.location?.pathname;

    switch (currentPath) {
        case AuthenticatedPaths.HOME:
            if (hasStudiesTab) {
                history.replace(AuthenticatedPaths.STUDIES);
            } else {
                history.replace(AuthenticatedPaths.SYSTEMS);
            }
            break;
        case AuthenticatedPaths.STUDIES:
            if (!hasStudiesTab) {
                history.replace(AuthenticatedPaths.SYSTEMS);
            }
            break;
        default:
            break;
    }
};

const UserSessionContextWrapper = ({ children }) => {
    const history = useHistory();
    const [enableVeevaIdUsernameUpdate, setEnableVeevaIdUsernameUpdate] = useState(false);
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);
    const [validSession, setValidSession] = useState(false);
    const [showInvalidSessionDialog, setShowInvalidSessionDialog] = useState(false);
    const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [systemsData, setSystemsData] = useState(undefined);
    const [showStudiesTab, setShowStudiesTab] = useState(false);

    const startFetchingHomeData = useCallback(async () => {
        const res = await getHomeData(messageLanguage);
        const hasStudiesTab = res?.some((tenant) =>
            tenant?.features?.some((feature) => feature === FeatureNames.STUDY),
        );

        handleDynamicRouting(history, hasStudiesTab);
        setSystemsData(res);
        setShowStudiesTab(hasStudiesTab);
        setLoading(false);
    }, [messageLanguage, history]);

    useEffect(() => {
        (async () => {
            const responseData = await getUserSessionContextGracefully();

            setEnableVeevaIdUsernameUpdate(responseData?.feature?.enableVeevaIdUsernameUpdate);

            if (responseData && responseData.vid_ww) {
                setValidSession(true);
                sessionStorage.setItem('vid_ww', responseData.vid_ww);
                startFetchingHomeData();
                return;
            }

            history.replace(`/`);
        })();
    }, [history, startFetchingHomeData]);

    const handleResponseSessionErrors = useCallback((response) => {
        if (isSessionTokenError(response)) {
            setShowInvalidSessionDialog(true);
            return true;
        }
        if (isExpiredSessionErrorResponse(response)) {
            setShowSessionExpiredDialog(true);
            return true;
        }

        return false;
    }, []);

    const contextValue = useMemo(
        () => ({
            handleResponseSessionErrors,
            enableVeevaIdUsernameUpdate,
            systemsData,
            showStudiesTab,
            loading,
        }),
        [
            handleResponseSessionErrors,
            enableVeevaIdUsernameUpdate,
            systemsData,
            showStudiesTab,
            loading,
        ],
    );

    if (!validSession) {
        return null;
    }

    return (
        <UserSessionContext.Provider value={contextValue}>
            {!isMobile() && <Header userSessionContext={contextValue} />}
            {loading ? <Loading backgroundColorDisabled /> : children}
            {showInvalidSessionDialog ? <InvalidSessionDialog /> : null}
            {showSessionExpiredDialog ? <SessionExpiredDialog /> : null}
        </UserSessionContext.Provider>
    );
};
UserSessionContextWrapper.propTypes = {
    children: PropTypes.node,
};

export default UserSessionContextWrapper;
